/**
 * portal/portalApi.js
 * All portal API calls go through here.
 * Token stored separately from admin token.
 */

const BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

/* ── Token helpers ── */
export const getPortalToken  = ()      => localStorage.getItem("forge_portal_token");
export const setPortalToken  = (token) => localStorage.setItem("forge_portal_token", token);
export const clearPortalToken = ()     => localStorage.removeItem("forge_portal_token");
export const isPortalLoggedIn = ()     => !!getPortalToken();

/* ── Base request ── */
async function req(path, opts = {}) {
  const token = getPortalToken();
  const res = await fetch(`${BASE}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...opts.headers,
    },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) { clearPortalToken(); window.location.href = "/portal/login"; return; }
  if (!res.ok) throw new Error(data.message || "Request failed");
  return data;
}

/* ── Multipart (for file uploads) ── */
async function upload(path, formData) {
  const token = getPortalToken();
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || "Upload failed");
  return data;
}

/* ── URL builder for file downloads (needs token in URL) ── */
export const portalFileUrl = (path) => {
  const token = getPortalToken();
  const base  = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
  return `${base}${path}${token ? `?token=${token}` : ""}`;
};

/* ── Auth ── */
export const portalAuth = {
  requestOtp:   (email)            => req("/portal/auth/request-otp",  { method:"POST", body:JSON.stringify({ email }) }),
  verifyOtp:    (email, otp)       => req("/portal/auth/verify-otp",   { method:"POST", body:JSON.stringify({ email, otp }) }),
  loginPassword:(email, password)  => req("/portal/auth/login",        { method:"POST", body:JSON.stringify({ email, password }) }),
  setPassword:  (password)         => req("/portal/auth/set-password", { method:"POST", body:JSON.stringify({ password }) }),
  register:     (payload)          => req("/portal/auth/register",     { method:"POST", body:JSON.stringify(payload) }),
};

/* ── One sign-in with Makua Retreats ── */
export const ssoAPI = {
  /** { url, targetName }: a one-time link that signs you in on the other site ("next": where to go there) */
  handoff:          (next)          => req("/portal/sso/handoff",           { method:"POST", body:JSON.stringify({ next }) }),
  /** { url, name }: Makua's website */
  other:            ()              => req("/portal/sso/other"),
  /** { email, from, here, hasAccount } */
  peek:             (code)          => req("/portal/sso/peek",              { method:"POST", body:JSON.stringify({ code }) }),
  /** { token, member, created } — 409 consent_needed when there's no account here yet */
  consume:          (code, consent) => req("/portal/sso/consume",           { method:"POST", body:JSON.stringify({ code, consent }) }),
  logoutEverywhere: ()              => req("/portal/sso/logout-everywhere", { method:"POST" }),
};

/* ── Portal data ── */
export const portalAPI = {
  me:          ()                        => req("/portal/me"),
  tasks:       ()                        => req("/portal/tasks"),
  application: ()                        => req("/portal/application"),
  documents:   ()                        => req("/portal/documents"),
  courses:     ()                        => req("/portal/courses"),
  course:      (id)                      => req(`/portal/courses/${id}`),
  lessonDone:  (courseId, lessonId, done) => req(`/portal/courses/${courseId}/lessons/${lessonId}/progress`, { method: "POST", body: JSON.stringify({ done }) }),
  replyToNote: (noteId, replyText, file) => {
    const fd = new FormData();
    fd.append("reply_text", replyText || "");
    if (file) fd.append("file", file);
    return upload(`/portal/notes/${noteId}/reply`, fd);
  },

  // Prescreening
  getPrescreening:    () => req("/portal/prescreening"),
  submitPrescreening: (answers) => req("/portal/prescreening", { method:"POST", body:JSON.stringify({ answers }) }),

  // Medical clearance
  getMedicalStatus: () => req("/portal/medical"),
  submitMedical: (files) => {
    const fd = new FormData();
    files.forEach(f => fd.append("documents", f));
    return upload("/portal/medical/submit", fd);
  },

  // Retreat offer
  getOffer: () => req("/portal/offer"),
  respondToOffer: (response) => req("/portal/offer/respond", { method:"POST", body:JSON.stringify({ response }) }),

  // Open date selection (batch activation)
  getOpenBlocks: () => req("/portal/blocks/open"),
  selectOffer: (retreatBlockId, roomTypeId) => req("/portal/offer/select", { method:"POST", body:JSON.stringify({ retreat_block_id: retreatBlockId, room_type_id: roomTypeId }) }),

  // Pathways booking (retreat → pathway → room → activity → diet)
  booking: {
    options: ()        => req("/portal/booking/options"),
    mine:    ()        => req("/portal/booking"),
    create:  (payload) => req("/portal/booking", { method:"POST",  body:JSON.stringify(payload) }),
    update:  (payload) => req("/portal/booking", { method:"PATCH", body:JSON.stringify(payload) }),
  },

  // Community forum (Discourse) — Forge signs members in
  community: {
    info: ()           => req("/portal/community"),
    sso:  (sso, sig)   => req("/portal/community/sso", { method:"POST", body:JSON.stringify({ sso, sig }) }),
  },

  // My cohort — visible once assigned to a retreat block
  getCohort: () => req("/portal/cohort"),

  // My payments — balance and installment history
  getPayments: () => req("/portal/payments"),

  // My retreat history — every retreat attended
  getRetreatHistory: () => req("/portal/retreat-history"),

  // ── My donations (giving history + receipts) ──
  getDonations: () => req("/portal/donations"),

  // ── Preferences / notifications / privacy ──
  getPreferences:    ()        => req("/portal/preferences"),
  updatePreferences: (payload) => req("/portal/preferences", { method:"PUT", body:JSON.stringify(payload) }),

  // ── Community stories (public endpoint, same data as the public map) ──
  getStories: (category) =>
    req(`/stories${category && category !== "all" ? `?category=${encodeURIComponent(category)}` : ""}`),

  // ── Message board ──
  board: {
    categories:  ()                    => req("/portal/board"),
    posts:       (categoryId)          => req(`/portal/board/${categoryId}/posts`),
    post:        (postId)              => req(`/portal/board/posts/${postId}`),
    createPost:  (categoryId, payload) => req(`/portal/board/${categoryId}/posts`, { method:"POST", body:JSON.stringify(payload) }),
    reply:       (postId, body)        => req(`/portal/board/posts/${postId}/replies`, { method:"POST", body:JSON.stringify({ body }) }),
    deletePost:  (postId)              => req(`/portal/board/posts/${postId}`, { method:"DELETE" }),
    deleteReply: (replyId)             => req(`/portal/board/replies/${replyId}`, { method:"DELETE" }),
    report:      (payload)             => req("/portal/board/report", { method:"POST", body:JSON.stringify(payload) }),
  },

  // ── Resource library ──
  resources: () => req("/portal/resources"),
  resourceDownloadUrl: (id) => {
    const base = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
    const token = localStorage.getItem("forge_portal_token");
    return `${base}/portal/resources/${id}/download${token ? `?token=${encodeURIComponent(token)}` : ""}`;
  },
};
